import { useState, useEffect } from 'react';
import { S } from '../styles.js';
import { useAuth } from '../context/AuthContext.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '';
const API = `${API_BASE}/api/recruiter`;

const STEPS = [
  { id: 1, label: 'Job Description', short: 'JD' },
  { id: 2, label: 'Candidate Resume', short: 'Resume' },
  { id: 3, label: 'Review & Generate', short: 'Generate' },
];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '4px 0 20px' }}>
      {STEPS.map((step, i) => {
        const done = current > step.id;
        const active = current === step.id;
        return (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={S.stepDot(active, done)}>
                {done ? '✓' : step.id}
              </div>
              <span style={{
                fontSize: 12, fontWeight: active ? 800 : 600,
                color: active ? '#2563eb' : done ? '#16a34a' : '#64748b',
                whiteSpace: 'nowrap', letterSpacing: '0.02em',
              }}>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ ...S.stepLine(done), margin: '0 8px', marginBottom: 22 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function RecruiterDashboard() {
  const { recruiter, logout, authFetch } = useAuth();
  const [step, setStep] = useState(1);
  const [jdText, setJdText] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [jobTitleOverride, setJobTitleOverride] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [tab, setTab] = useState('new');
  const [copied, setCopied] = useState(false);
  const [jdFileName, setJdFileName] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (tab === 'list' || tab === 'candidates' || tab === 'analytics') {
      fetchInterviews();
    }
  }, [tab]);

  async function fetchInterviews() {
    try {
      const r = await authFetch(`${API}/interviews`);
      const data = await r.json();
      setInterviews(Array.isArray(data) ? data : []);
    } catch { setInterviews([]); }
  }

  const [resumeUrl, setResumeUrl] = useState('');

  async function handleFile(kind, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await authFetch(`${API}/parse-document`, {
        method: 'POST',
        body: formData,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to parse document');

      if (kind === 'jd') { setJdText(data.text); setJdFileName(file.name); }
      else {
        setResumeText(data.text);
        setResumeFileName(file.name);
        if (data.s3Url) setResumeUrl(data.s3Url);
      }
    } catch (err) {
      setError(`Failed to read ${file.name}: ${err.message}`);
    }
  }

  function goNext() {
    setError('');
    if (step === 1 && !jdText.trim()) { setError('Please provide the job description to continue.'); return; }
    if (step === 2 && !resumeText.trim()) { setError('Please provide the candidate resume to continue.'); return; }
    setStep((s) => s + 1);
  }

  function goBack() { setError(''); setStep((s) => s - 1); }

  async function handleGenerate() {
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await authFetch(`${API}/setup`, {
        method: 'POST',
        body: JSON.stringify({ jdText, resumeText, candidateName, jobTitleOverride, resumeUrl }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Setup failed');
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  function startOver() {
    setStep(1); setJdText(''); setResumeText(''); setResumeUrl(''); setCandidateName(''); setJobTitleOverride('');
    setResult(null); setError(''); setJdFileName(''); setResumeFileName('');
  }

  function copyLink() {
    navigator.clipboard.writeText(result.candidateLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="sidebar-layout" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', color: '#0f172a', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Left Collapsible Sidebar Navigation */}
      <aside style={{
        width: isCollapsed ? 76 : 260, flexShrink: 0, background: '#ffffff', borderRight: '1px solid #e2e8f0',
        padding: isCollapsed ? '24px 12px' : '24px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        height: '100vh', position: 'sticky', top: 0, transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '4px 0 24px rgba(0,0,0,0.02)', zIndex: 20
      }}>
        <div>
          {/* Top Brand Logo Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'flex-start',
            marginBottom: 28, padding: isCollapsed ? '0' : '0 4px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, color: '#ffffff', boxShadow: '0 4px 14px rgba(37,99,235,0.25)', flexShrink: 0
              }}>🤖</div>
              {!isCollapsed && (
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>Luminoid AI</h2>
                  <p style={{ margin: 0, fontSize: 11, color: '#64748b', fontWeight: 600 }}>Recruiter Portal</p>
                </div>
              )}
            </div>
          </div>

          {/* Primary Action Button */}
          <button
            onClick={() => { setTab('new'); startOver(); }}
            title="New Interview"
            style={{
              width: '100%', padding: isCollapsed ? '12px' : '11px 16px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#ffffff',
              fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 24,
              boxShadow: '0 4px 16px rgba(37,99,235,0.22)'
            }}
          >
            <span style={{ fontSize: 16 }}>＋</span> {!isCollapsed && 'New Interview'}
          </button>

          {/* Navigation Links */}
          <nav style={{ display: 'grid', gap: 4 }}>
            {[
              { id: 'new', label: 'Dashboard', icon: '📊' },
              { id: 'list', label: 'Interviews', icon: '📋' },
              { id: 'candidates', label: 'Candidates', icon: '👤' },
              { id: 'analytics', label: 'Analytics', icon: '📈' },
            ].map((navItem) => {
              const active = tab === navItem.id || (tab === 'new' && navItem.id === 'new');
              return (
                <button
                  key={navItem.id}
                  onClick={() => setTab(navItem.id)}
                  title={navItem.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    padding: isCollapsed ? '12px' : '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    fontSize: 13, fontWeight: active ? 800 : 600, transition: 'all 0.15s ease',
                    background: active ? '#eff6ff' : 'transparent',
                    color: active ? '#2563eb' : '#64748b',
                    boxShadow: active ? 'inset 3px 0 0 #2563eb' : 'none'
                  }}
                >
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{navItem.icon}</span>
                  {!isCollapsed && navItem.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Bottom Recruiter Profile & Collapse Toggle */}
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{
            padding: isCollapsed ? '10px 6px' : '12px 14px', borderRadius: 14, background: '#f8fafc',
            border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: '#2563eb',
                color: '#ffffff', fontWeight: 800, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                {recruiter?.name?.charAt(0).toUpperCase() || 'R'}
              </div>
              {!isCollapsed && (
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{recruiter?.name || 'Recruiter'}</p>
                  <p style={{ margin: 0, fontSize: 10, color: '#64748b' }}>Account</p>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <button onClick={logout} title="Logout" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>
                🚪
              </button>
            )}
          </div>

          {/* Bottom Collapse Toggle Row */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between',
              width: '100%', padding: isCollapsed ? '10px 0' : '10px 14px', borderRadius: 12,
              border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155',
              fontSize: 12, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s ease',
              boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
            }}
          >
            {!isCollapsed && <span style={{ letterSpacing: '0.01em' }}>Collapse Sidebar</span>}
            <span style={{ fontSize: 14, fontWeight: 900, color: '#2563eb', display: 'flex', alignItems: 'center' }}>
              {isCollapsed ? '➔' : '◀'}
            </span>
          </button>
        </div>
      </aside>

      {/* Right Main Application Canvas */}
      <main style={{ flex: 1, padding: '28px 28px', overflowY: 'auto' }}>

        {/* ── New Interview Wizard (Review & Generate Figma Layout) ── */}
        {tab === 'new' && !result && (
          <div className="fade-in" style={{ width: '100%' }}>

            {/* Header */}
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 900, color: '#0f172a' }}>
                {step === 3 ? 'Review & Generate' : 'Create New Interview'}
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                Confirm the interview parameters before deploying the AI Agent.
              </p>
            </div>

            <StepIndicator current={step} />

            <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #e2e8f0', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

              {/* Step 1 — Job Description */}
              {step === 1 && (
                <div className="fade-in">
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Upload Job Description</h2>
                  <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>
                    Provide the job description to ground the AI in role requirements.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: 6 }}>Candidate Name <span style={{ color: '#64748b', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
                      <input
                        value={candidateName}
                        onChange={(e) => setCandidateName(e.target.value)}
                        placeholder="e.g. Jane Doe"
                        style={S.input}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: 6 }}>Job Title / Role <span style={{ color: '#64748b', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
                      <input
                        value={jobTitleOverride}
                        onChange={(e) => setJobTitleOverride(e.target.value)}
                        placeholder="e.g. Senior Product Designer"
                        style={S.input}
                      />
                    </div>
                  </div>

                  {/* Figma Drag & Drop Dropzone */}
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files?.[0]) {
                        const event = { target: { files: e.dataTransfer.files } };
                        handleFile('jd', event);
                      }
                    }}
                    style={{
                      border: '2px dashed #bfdbfe', borderRadius: 20, background: '#f8fafc',
                      padding: '40px 24px', textAlign: 'center', marginBottom: 20, cursor: 'pointer',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%', background: '#eff6ff',
                      color: '#2563eb', fontSize: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}>📤</div>
                    <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Click to upload or drag and drop</h3>
                    <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b' }}>PDF, DOCX, or TXT (Max 5MB)</p>

                    <label style={{
                      padding: '10px 22px', borderRadius: 10, background: '#2563eb',
                      color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-block'
                    }}>
                      Select File
                      <input type="file" accept=".txt,.md,.pdf,.docx" onChange={(e) => handleFile('jd', e)} style={{ display: 'none' }} />
                    </label>
                  </div>

                  {/* Uploaded File Status Pill */}
                  {jdFileName && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14,
                      padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20
                    }}>
                      <span style={{ fontSize: 20 }}>✅</span>
                      <div>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#15803d' }}>{jdFileName}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#16a34a' }}>Uploaded & Parsed Successfully</p>
                      </div>
                    </div>
                  )}

                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: 8 }}>
                    Job Description Text Content
                  </label>
                  <textarea
                    value={jdText}
                    onChange={(e) => setJdText(e.target.value)}
                    style={{ ...S.textarea, minHeight: 180 }}
                    placeholder="Paste or edit job description details here…"
                  />

                  {error && <div style={S.error}><span>⚠</span> {error}</div>}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={goNext} style={{
                      padding: '12px 24px', borderRadius: 12, border: 'none',
                      background: '#2563eb', color: '#ffffff', fontWeight: 800, fontSize: 14, cursor: 'pointer'
                    }}>
                      Proceed to Resume →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 — Candidate Resume */}
              {step === 2 && (
                <div className="fade-in">
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Upload Candidate Resume</h2>
                  <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>
                    Provide the candidate's resume to ground the AI in their background.
                  </p>

                  {/* Figma Drag & Drop Dropzone */}
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files?.[0]) {
                        const event = { target: { files: e.dataTransfer.files } };
                        handleFile('resume', event);
                      }
                    }}
                    style={{
                      border: '2px dashed #bfdbfe', borderRadius: 20, background: '#f8fafc',
                      padding: '40px 24px', textAlign: 'center', marginBottom: 20, cursor: 'pointer',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%', background: '#eff6ff',
                      color: '#2563eb', fontSize: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}>📄</div>
                    <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Click to upload or drag and drop</h3>
                    <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b' }}>PDF, DOCX, or TXT (Max 5MB)</p>

                    <label style={{
                      padding: '10px 22px', borderRadius: 10, background: '#2563eb',
                      color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-block'
                    }}>
                      Select File
                      <input type="file" accept=".txt,.md,.pdf,.docx" onChange={(e) => handleFile('resume', e)} style={{ display: 'none' }} />
                    </label>
                  </div>

                  {/* Uploaded File Status Pill */}
                  {resumeFileName && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14,
                      padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#15803d' }}>{resumeFileName}</p>
                          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#16a34a' }}>Uploaded to AWS S3 & Parsed Successfully</p>
                        </div>
                      </div>
                      {resumeUrl && (
                        <span style={{
                          fontSize: 11, fontWeight: 800, color: '#2563eb', background: '#eff6ff',
                          border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: 8
                        }}>
                          ☁️ Saved to S3
                        </span>
                      )}
                    </div>
                  )}

                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: 8 }}>
                    Candidate Resume Text Content
                  </label>
                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    style={{ ...S.textarea, minHeight: 180 }}
                    placeholder="Paste or view candidate resume details here…"
                  />

                  {error && <div style={S.error}><span>⚠</span> {error}</div>}

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                    <button onClick={goBack} style={{
                      padding: '10px 18px', borderRadius: 10, border: '1px solid #cbd5e1',
                      background: '#ffffff', color: '#334155', fontWeight: 700, fontSize: 13, cursor: 'pointer'
                    }}>← Back</button>
                    <button onClick={goNext} style={{
                      padding: '12px 24px', borderRadius: 12, border: 'none',
                      background: '#2563eb', color: '#ffffff', fontWeight: 800, fontSize: 14, cursor: 'pointer'
                    }}>Proceed to Review →</button>
                  </div>
                </div>
              )}

              {/* Step 3 — Review & Generate */}
              {step === 3 && (
                <div className="fade-in">
                  <h2 style={{ ...S.sectionTitle, fontSize: 17, marginBottom: 6 }}>🚀 Review & Generate</h2>
                  <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b8aad', lineHeight: 1.6 }}>
                    Everything looks good. Click Generate to run the full AI pipeline and create the interview link.
                  </p>

                  {/* Summary cards */}
                  <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                    <ReviewItem
                      icon="📋" label="Job Description"
                      value={jdFileName || `${jdText.slice(0, 80).trim()}…`}
                      onEdit={() => { setError(''); setStep(1); }}
                    />
                    <ReviewItem
                      icon="👤" label="Candidate Resume"
                      value={resumeFileName || `${resumeText.slice(0, 80).trim()}…`}
                      onEdit={() => { setError(''); setStep(2); }}
                    />
                    {candidateName && (
                      <ReviewItem icon="🏷️" label="Candidate Name" value={candidateName} onEdit={() => { setError(''); setStep(1); }} />
                    )}
                  </div>

                  {/* Pipeline preview */}
                  <div style={{ padding: '16px 20px', borderRadius: 14, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 24 }}>
                    <p style={{ margin: '0 0 10px', fontSize: 11, color: '#2563eb', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>AI Pipeline Workflow</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {['JD Analyzer', 'Resume Parser', 'Matcher', 'Question Generator'].map((s, i, arr) => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 700, background: '#ffffff', padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}>{s}</span>
                          {i < arr.length - 1 && <span style={{ color: '#2563eb', fontSize: 16, fontWeight: 900 }}>→</span>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {error && <div style={S.error}><span>⚠</span> {error}</div>}

                  <div style={{ ...S.row, justifyContent: 'space-between', marginTop: 4 }}>
                    <button onClick={goBack} style={S.btnSm}>← Back</button>
                    <button onClick={handleGenerate} disabled={loading} style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}>
                      {loading ? (
                        <>
                          <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(3,16,31,0.3)', borderTopColor: '#03101f', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                          Running AI Pipeline…
                        </>
                      ) : '🚀 Generate Interview'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Result View (Modern Figma Layout) ── */}
        {tab === 'new' && result && (
          <div className="fade-in" style={{ width: '100%', display: 'grid', gap: 20 }}>

            {/* Top Action Bar */}
            <div style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '20px 28px',
              display: 'flex',
              alignItems: 'center',
              justify: 'space-between',
              gap: 24,
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CANDIDATE EVALUATION</span>
                <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#0f172a', wordBreak: 'break-word' }}>{result.candidateName || 'Candidate Profile'}</h2>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0 }}>
                <button onClick={copyLink} style={{
                  padding: '11px 22px', borderRadius: 12, border: 'none',
                  background: copied ? '#16a34a' : '#2563eb', color: '#ffffff',
                  fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: '0 4px 14px rgba(37,99,235,0.25)'
                }}>
                  {copied ? '✅ Link Copied!' : '🔗 Copy Shareable Link'}
                </button>
                <button onClick={startOver} style={{
                  padding: '11px 20px', borderRadius: 12, border: '1px solid #cbd5e1',
                  background: '#ffffff', color: '#334155', fontWeight: 700, fontSize: 13, cursor: 'pointer'
                }}>
                  ＋ New Evaluation
                </button>
              </div>
            </div>

            {/* Main Evaluation Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>

              {/* Left Sidebar Profile */}
              <div style={{
                background: '#ffffff',
                borderRadius: 20,
                padding: '28px 24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <div style={{
                  width: 84, height: 84, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#ffffff', fontSize: 32, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14, boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)',
                }}>
                  {result.candidateName ? result.candidateName.charAt(0).toUpperCase() : 'C'}
                </div>

                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                  {result.candidateName || 'Candidate'}
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  {result.jobTitle || 'Role Not Specified'}
                </p>

                {/* Score Circular Badge */}
                <div style={{
                  width: '100%',
                  background: '#f8fafc',
                  borderRadius: 16,
                  padding: '20px',
                  border: '1px solid #f1f5f9',
                  marginBottom: 16,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overall Alignment</span>
                  <div style={{ fontSize: 44, fontWeight: 900, color: result.matchScore >= 70 ? '#16a34a' : result.matchScore >= 50 ? '#d97706' : '#dc2626', margin: '4px 0 0' }}>
                    {result.matchScore}<span style={{ fontSize: 20, color: '#94a3b8' }}>%</span>
                  </div>
                </div>

                <div style={{ width: '100%', height: 1, background: '#f1f5f9', margin: '8px 0 16px' }} />

                <div style={{ width: '100%', textAlign: 'left' }}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Candidate Interview Link</p>
                  <p style={{ margin: 0, fontSize: 11, color: '#2563eb', wordBreak: 'break-all', fontWeight: 600, background: '#eff6ff', padding: '8px 12px', borderRadius: 8 }}>
                    {result.candidateLink}
                  </p>
                </div>
              </div>

              {/* Right Summary & Questions Panel */}
              <div style={{ display: 'grid', gap: 20 }}>

                {/* AI Executive Summary */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: 20,
                  padding: '24px 28px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 18 }}>🧠</span>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>AI Executive Summary</h4>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
                    {result.aiSummary}
                  </p>
                </div>

                {/* Generated Questions List */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: 20,
                  padding: '24px 28px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <div>
                      <h4 style={{ margin: '0 0 2px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Generated Technical Questions</h4>
                      <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{result.questions?.length || 0} questions tailored to JD and Resume experience</p>
                    </div>
                    <button
                      onClick={() => {
                        const qText = prompt('Enter custom interview question:');
                        if (!qText) return;
                        const skill = prompt('Enter skill category (optional):') || 'Technical Competency';
                        const newQ = {
                          id: `Q${(result.questions?.length || 0) + 1}`,
                          skill,
                          difficulty: 'Intermediate',
                          type: 'technical',
                          question: qText.trim()
                        };
                        setResult(prev => ({
                          ...prev,
                          questions: [...(prev.questions || []), newQ]
                        }));
                      }}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: '1px solid #93c5fd',
                        background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 12, cursor: 'pointer'
                      }}
                    >
                      ＋ Add Custom Question
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: 12 }}>
                    {result.questions?.map((q, i) => (
                      <div key={i} style={{
                        padding: '14px 18px', borderRadius: 12,
                        border: '1px solid #f1f5f9', background: '#f8fafc',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>#{i + 1}</span>
                            {q.skill && !/domain expertise|domain experience|operations|professional competencies|role competencies|core skills/i.test(q.skill) && (
                              <span style={{
                                fontSize: 11, fontWeight: 700, color: '#2563eb',
                                background: '#dbeafe', padding: '2px 8px', borderRadius: 6
                              }}>
                                {q.skill}
                              </span>
                            )}
                          </div>
                          <p style={{ margin: 0, color: '#1e293b', fontSize: 14, lineHeight: 1.6, fontWeight: 500 }}>
                            {q.question}
                          </p>
                        </div>
                        <button
                          title="Remove Question"
                          onClick={() => {
                            setResult(prev => ({
                              ...prev,
                              questions: prev.questions.filter((_, idx) => idx !== i)
                            }));
                          }}
                          style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '2px' }}
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

            </div>

          </div>
        )}

        {/* ── Candidate Resume Inspection Tab ── */}
        {tab === 'candidates' && (
          <div className="fade-in" style={{ width: '100%', display: 'grid', gap: 20 }}>
            <div style={{
              background: '#ffffff', borderRadius: 16, padding: '18px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Candidate Resumes & Profiles</h2>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Inspect full candidate resumes and AI match scores</p>
              </div>
              <button onClick={fetchInterviews} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1',
                background: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: 12, cursor: 'pointer'
              }}>
                ↻ Refresh List
              </button>
            </div>

            {interviews.length === 0 ? (
              <div style={{ background: '#ffffff', borderRadius: 20, padding: '52px 24px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: 42, marginBottom: 12 }}>👤</p>
                <p style={{ color: '#64748b', fontSize: 14 }}>No candidate profiles found.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 18 }}>
                {interviews.map((iv) => (
                  <div key={iv.interviewId} style={{
                    background: '#ffffff', borderRadius: 20, padding: '24px',
                    border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%', background: '#2563eb',
                          color: '#ffffff', fontWeight: 800, fontSize: 18,
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {iv.candidateName ? iv.candidateName.charAt(0).toUpperCase() : 'C'}
                        </div>
                        <div>
                          <h3 style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{iv.candidateName || 'Candidate'}</h3>
                          <p style={{ margin: 0, fontSize: 13, color: '#2563eb', fontWeight: 600 }}>💼 {iv.jobTitle}</p>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Match Score</span>
                        <div style={{ fontSize: 22, fontWeight: 900, color: iv.matchScore >= 70 ? '#16a34a' : '#d97706' }}>
                          {iv.matchScore}%
                        </div>
                      </div>
                    </div>

                    {/* Clean Candidate Detail Row & Download Resume Action */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 8,
                        background: iv.status === 'completed' ? '#dcfce7' : '#dbeafe',
                        color: iv.status === 'completed' ? '#15803d' : '#1d4ed8'
                      }}>
                        STATUS: {iv.status?.toUpperCase()}
                      </span>

                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={() => {
                            if (iv.resumeUrl) {
                              const a = document.createElement('a');
                              a.href = iv.resumeUrl;
                              a.target = '_blank';
                              a.rel = 'noopener noreferrer';
                              a.download = '';
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            } else {
                              alert('No uploaded file URL available for this candidate resume.');
                            }
                          }}
                          style={{
                            padding: '10px 20px', borderRadius: 10, border: '1px solid #bfdbfe',
                            background: '#eff6ff', color: '#2563eb', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(37,99,235,0.1)'
                          }}
                        >
                          📥 Download Original Resume (PDF/DOCX)
                        </button>

                        {iv.videoUrl && iv.videoUrl.startsWith('http') && (
                          <button
                            onClick={() => window.open(iv.videoUrl, '_blank')}
                            style={{
                              padding: '10px 18px', borderRadius: 10, border: '1px solid #fed7aa',
                              background: '#fff7ed', color: '#c2410c', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(194,65,12,0.1)'
                            }}
                          >
                            🎥 Watch Recording (S3)
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setResult({
                              interviewId: iv.interviewId,
                              token: iv.token,
                              candidateLink: iv.candidateLink,
                              candidateName: iv.candidateName,
                              jobTitle: iv.jobTitle,
                              matchScore: iv.matchScore,
                              aiSummary: iv.aiSummary,
                              questions: []
                            });
                            setTab('new');
                          }}
                          style={{
                            padding: '10px 18px', borderRadius: 10, border: 'none',
                            background: '#2563eb', color: '#ffffff', fontWeight: 800, fontSize: 13, cursor: 'pointer'
                          }}
                        >
                          Inspect AI Evaluation →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Analytics Dashboard View ── */}
        {tab === 'analytics' && (
          <div className="fade-in" style={{ width: '100%', display: 'grid', gap: 24 }}>
            <div style={{
              background: '#ffffff', borderRadius: 18, padding: '20px 28px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Talent Pipeline Analytics</h2>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Real-time overview of candidate evaluation throughput and alignment scores</p>
              </div>
              <button onClick={fetchInterviews} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1',
                background: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: 12, cursor: 'pointer'
              }}>
                ↻ Refresh Metrics
              </button>
            </div>

            {/* Metrics KPI Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {[
                { label: 'Total Interviews', value: interviews.length, color: '#2563eb', bg: '#eff6ff' },
                { label: 'Completed Reviews', value: interviews.filter(i => i.status === 'completed').length, color: '#16a34a', bg: '#f0fdf4' },
                { label: 'In Progress', value: interviews.filter(i => i.status === 'in_progress').length, color: '#d97706', bg: '#fffbeb' },
                { label: 'Avg Alignment', value: `${interviews.length ? Math.round(interviews.reduce((acc, i) => acc + (i.matchScore || 0), 0) / interviews.length) : 0}%`, color: '#7c3aed', bg: '#f5f3ff' },
              ].map((kpi) => (
                <div key={kpi.label} style={{
                  background: '#ffffff', borderRadius: 16, padding: '20px',
                  border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                }}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {kpi.label}
                  </p>
                  <p style={{ margin: 0, fontSize: 28, fontWeight: 900, color: kpi.color }}>
                    {kpi.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Visual Funnel & Alignment Breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

              {/* Pipeline Status Breakdown */}
              <div style={{
                background: '#ffffff', borderRadius: 20, padding: '24px',
                border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
              }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Evaluation Status Funnel</h3>
                <div style={{ display: 'grid', gap: 14 }}>
                  {[
                    { label: 'Completed Interviews', count: interviews.filter(i => i.status === 'completed').length, pct: interviews.length ? Math.round((interviews.filter(i => i.status === 'completed').length / interviews.length) * 100) : 0, color: '#16a34a' },
                    { label: 'Active / In Progress', count: interviews.filter(i => i.status === 'in_progress').length, pct: interviews.length ? Math.round((interviews.filter(i => i.status === 'in_progress').length / interviews.length) * 100) : 0, color: '#2563eb' },
                    { label: 'Pending Candidates', count: interviews.filter(i => i.status === 'pending').length, pct: interviews.length ? Math.round((interviews.filter(i => i.status === 'pending').length / interviews.length) * 100) : 0, color: '#94a3b8' },
                  ].map((item) => (
                    <div key={item.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#334155' }}>
                        <span>{item.label}</span>
                        <span>{item.count} ({item.pct}%)</span>
                      </div>
                      <div style={{ height: 10, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${item.pct}%`, background: item.color, borderRadius: 999, transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Candidates by Score */}
              <div style={{
                background: '#ffffff', borderRadius: 20, padding: '24px',
                border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
              }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Top Candidates by Alignment</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  {interviews.slice(0, 4).map((iv) => (
                    <div key={iv.interviewId} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{iv.candidateName || 'Candidate'}</p>
                        <p style={{ margin: 0, fontSize: 12, color: '#2563eb', fontWeight: 600 }}>💼 {iv.jobTitle}</p>
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 900, color: iv.matchScore >= 70 ? '#16a34a' : '#d97706' }}>
                        {iv.matchScore}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </div>
        )}

        {/* ── All Interviews Tab (Modern Figma Cards) ── */}
        {tab === 'list' && (
          <div className="fade-in" style={{ width: '100%', display: 'grid', gap: 20 }}>
            <div style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '18px 24px',
              display: 'flex',
              alignItems: 'center',
              justify: 'space-between',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid #e2e8f0',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>All Candidate Evaluations</h2>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Overview of all generated interview links and evaluation reports</p>
              </div>
              <button onClick={fetchInterviews} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1',
                background: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: 12, cursor: 'pointer'
              }}>
                ↻ Refresh List
              </button>
            </div>

            {interviews.length === 0 ? (
              <div style={{
                background: '#ffffff', borderRadius: 20, padding: '52px 24px',
                textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
              }}>
                <p style={{ fontSize: 42, marginBottom: 12 }}>📭</p>
                <p style={{ color: '#64748b', fontSize: 14 }}>No interviews created yet.</p>
                <button onClick={() => setTab('new')} style={{
                  marginTop: 16, padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: '#2563eb', color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: 'pointer'
                }}>
                  ＋ Create First Interview
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                {interviews.map((iv) => (
                  <div key={iv.interviewId} style={{
                    background: '#ffffff', borderRadius: 16, padding: '20px 24px',
                    border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 20, alignItems: 'center'
                  }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: '#ffffff', fontSize: 20, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {iv.candidateName ? iv.candidateName.charAt(0).toUpperCase() : 'C'}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{iv.candidateName || 'Candidate'}</h4>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '2px 8px', borderRadius: 6,
                          background: iv.status === 'completed' ? '#dcfce7' : '#dbeafe',
                          color: iv.status === 'completed' ? '#15803d' : '#1d4ed8',
                        }}>
                          {iv.status?.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p style={{ margin: '0 0 6px', fontSize: 13, color: '#2563eb', fontWeight: 600 }}>
                        💼 {iv.jobTitle || 'Role Not Specified'}
                      </p>

                      {/* Display evaluated answers summary if report exists */}
                      {iv.report ? (
                        <div style={{ background: '#f0fdf4', padding: '6px 10px', borderRadius: 8, border: '1px solid #bbf7d0', margin: '6px 0' }}>
                          <p style={{ margin: 0, fontSize: 12, color: '#15803d', fontWeight: 700 }}>
                            📊 Evaluation Result: {iv.report.overall_score}/10 — Recommendation: {iv.report.recommendation}
                          </p>
                        </div>
                      ) : (
                        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                          Created: {new Date(iv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>

                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Match</span>
                        <span style={{ fontSize: 20, fontWeight: 900, color: iv.matchScore >= 70 ? '#16a34a' : iv.matchScore >= 50 ? '#d97706' : '#dc2626' }}>
                          {iv.matchScore}%
                        </span>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(iv.candidateLink)}
                        style={{
                          padding: '6px 12px', borderRadius: 8, border: '1px solid #bfdbfe',
                          background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 11, cursor: 'pointer'
                        }}
                      >
                        🔗 Copy Link
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function ReviewItem({ icon, label, value, onEdit }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 14, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
      <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: '0 0 2px', fontSize: 11, color: '#2563eb', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</p>
        <p style={{ margin: 0, fontSize: 14, color: '#0f172a', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
      </div>
      <button onClick={onEdit} style={{
        padding: '6px 14px', borderRadius: 8, border: '1px solid #cbd5e1',
        background: '#ffffff', color: '#334155', fontWeight: 700, fontSize: 12, cursor: 'pointer'
      }}>Edit</button>
    </div>
  );
}
